import React, { useState, useEffect } from 'react';
import { FileText, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Brand {
  id: string;
  name: string;
  primary_color: string;
  secondary_color: string;
}

interface TermsAndConditions {
  id: string;
  brand_id: string;
  service_types: string[];
  terms_content: string;
  effective_date: string;
  is_active: boolean;
  created_at: string;
}

interface TermsManagementProps {
  brands: Brand[];
}

export function TermsManagement({ brands }: TermsManagementProps) {
  const [terms, setTerms] = useState<TermsAndConditions[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const [formData, setFormData] = useState({
    brand_id: '',
    service_types: [] as string[],
    terms_content: '',
    effective_date: new Date().toISOString().split('T')[0],
    is_active: true
  });

  useEffect(() => {
    loadTerms();
  }, []);

  const loadTerms = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('terms_and_conditions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTerms(data || []);
    } catch (error) {
      console.error('Error loading terms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (term: TermsAndConditions) => {
    setEditingId(term.id);
    setFormData({
      brand_id: term.brand_id,
      service_types: term.service_types || [],
      terms_content: term.terms_content,
      effective_date: new Date(term.effective_date).toISOString().split('T')[0],
      is_active: term.is_active
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      brand_id: brands[0]?.id || '',
      service_types: [],
      terms_content: '',
      effective_date: new Date().toISOString().split('T')[0],
      is_active: true
    });
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        const { error } = await supabase
          .from('terms_and_conditions')
          .update(formData)
          .eq('id', editingId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('terms_and_conditions')
          .insert(formData);

        if (error) throw error;
      }

      await loadTerms();
      setEditingId(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving terms:', error);
      alert('Error saving terms. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this terms document?')) return;

    try {
      const { error } = await supabase
        .from('terms_and_conditions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadTerms();
    } catch (error) {
      console.error('Error deleting terms:', error);
      alert('Error deleting terms. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsCreating(false);
    setFormData({
      brand_id: '',
      service_types: [],
      terms_content: '',
      effective_date: new Date().toISOString().split('T')[0],
      is_active: true
    });
  };

  const toggleServiceType = (type: string) => {
    setFormData(prev => ({
      ...prev,
      service_types: prev.service_types.includes(type)
        ? prev.service_types.filter(t => t !== type)
        : [...prev.service_types, type]
    }));
  };

  const getBrandName = (brandId: string) => {
    return brands.find(b => b.id === brandId)?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading terms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <FileText className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900">Terms & Conditions</h2>
        </div>
        {!isCreating && !editingId && (
          <button
            onClick={handleCreate}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>New Terms</span>
          </button>
        )}
      </div>

      {(isCreating || editingId) && (
        <div className="bg-white p-6 rounded-lg shadow-md border-2 border-blue-500">
          <h3 className="text-lg font-semibold mb-4">
            {editingId ? 'Edit Terms & Conditions' : 'Create New Terms & Conditions'}
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand
              </label>
              <select
                value={formData.brand_id}
                onChange={(e) => setFormData({ ...formData, brand_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {brands.map(brand => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applicable Service Types
              </label>
              <div className="flex flex-wrap gap-2">
                {['web', 'marketing', 'seo', 'ppc', 'email', 'cro'].map(type => (
                  <label key={type} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.service_types.includes(type)}
                      onChange={() => toggleServiceType(type)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
                    />
                    <span className="text-sm text-gray-700 capitalize">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Effective Date
              </label>
              <input
                type="date"
                value={formData.effective_date}
                onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Terms Content (HTML supported)
              </label>
              <textarea
                value={formData.terms_content}
                onChange={(e) => setFormData({ ...formData, terms_content: e.target.value })}
                rows={12}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-none"
                placeholder="Enter terms and conditions content... HTML tags are supported."
              />
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {terms.map(term => (
          <div
            key={term.id}
            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {getBrandName(term.brand_id)}
                  </h3>
                  {term.is_active ? (
                    <span className="px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold text-gray-700 bg-gray-100 rounded-full">
                      Inactive
                    </span>
                  )}
                </div>

                <div className="space-y-1 text-sm text-gray-600">
                  <p>
                    <span className="font-medium">Service Types:</span>{' '}
                    {term.service_types.length > 0
                      ? term.service_types.join(', ')
                      : 'All services'}
                  </p>
                  <p>
                    <span className="font-medium">Effective Date:</span>{' '}
                    {new Date(term.effective_date).toLocaleDateString()}
                  </p>
                  <p>
                    <span className="font-medium">Created:</span>{' '}
                    {new Date(term.created_at).toLocaleDateString()}
                  </p>
                </div>

                <div className="mt-3 p-3 bg-gray-50 rounded max-h-40 overflow-y-auto">
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: term.terms_content.substring(0, 300) + (term.terms_content.length > 300 ? '...' : '') }}
                  />
                </div>
              </div>

              {!isCreating && !editingId && (
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(term)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(term.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {terms.length === 0 && !isCreating && (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No terms and conditions created yet</p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Create First Terms</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
